import { useState } from 'react';

import type { SettlementPoint, StatPeriod } from '@solar/api-client';
import { ErrorBox, Spinner, useAsync, won } from '@solar/ui';

import { api } from '../api';
import { barWidth, dateKeys, yearOptions } from './statShared';

/**
 * 정산 통계 — 레거시 stat/money.blade.php 파리티.
 *
 * 시계열(총 금액=price 합, 실 금액=payment 합) + 팀별 완료금액 표.
 * 레거시 Morris 차트는 CSS 막대로 대체하고, 미배정 건수(no_team_count)는
 * 레거시가 응답에만 담고 그리지 않던 값이라 표에 열로 노출한다.
 */
export function StatSettlement() {
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [period, setPeriod] = useState<StatPeriod>('day');

  const stats = useAsync(
    () => api.settlementStats({ year, month, period }),
    [year, month, period],
  );

  const data = stats.data;
  const byDate = new Map((data?.series ?? []).map((p) => [p.date, p]));
  const series: SettlementPoint[] = dateKeys(year, month, period).map(
    (date) => byDate.get(date) ?? { date, total_price: 0, total_payment: 0, no_team_count: 0 },
  );
  const sumPrice = series.reduce((acc, p) => acc + p.total_price, 0);
  const sumPayment = series.reduce((acc, p) => acc + p.total_payment, 0);
  const max = Math.max(...series.map((p) => p.total_price), 0);

  const teams = data?.teams ?? [];
  const teamSum = teams.reduce((acc, t) => acc + t.amount, 0);
  const teamMax = Math.max(...teams.map((t) => t.amount), 0);

  return (
    <div className="admin__body admin__body--single">
      <section className="admin__list">
        <div className="toolbar">
          <select value={year} onChange={(e) => setYear(Number(e.target.value))}>
            {yearOptions().map((y) => (
              <option key={y} value={y}>
                {y}년
              </option>
            ))}
          </select>
          {period === 'day' && (
            <select value={month} onChange={(e) => setMonth(Number(e.target.value))}>
              {Array.from({ length: 12 }, (_, i) => i + 1).map((m) => (
                <option key={m} value={m}>
                  {m}월
                </option>
              ))}
            </select>
          )}
          <select value={period} onChange={(e) => setPeriod(e.target.value as StatPeriod)}>
            <option value="day">일별</option>
            <option value="month">월별</option>
          </select>
          <span className="stat-label">
            총 금액 {won(sumPrice)} · 실 금액 {won(sumPayment)}
          </span>
        </div>

        {stats.loading && <Spinner />}
        {stats.error && <ErrorBox message={stats.error} onRetry={stats.reload} />}

        {data && (
          <>
            <table className="grid stat-table">
              <thead>
                <tr>
                  <th>날짜</th>
                  <th className="num">총 금액</th>
                  <th className="num">실 금액</th>
                  <th className="num">미배정 건수</th>
                  <th className="bar-col" aria-label="막대 그래프" />
                </tr>
              </thead>
              <tbody>
                {series.map((p) => (
                  <tr key={p.date}>
                    <td>{p.date}</td>
                    <td className="num">{won(p.total_price)}</td>
                    <td className="num">{won(p.total_payment)}</td>
                    <td className="num">{p.no_team_count.toLocaleString()} 건</td>
                    <td className="bar-col">
                      <span className="bar" style={{ width: barWidth(p.total_price, max) }} />
                      <span
                        className="bar bar--done"
                        style={{ width: barWidth(p.total_payment, max) }}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            <h3 className="section">팀별 — 완료금액 (합계 {won(teamSum)})</h3>
            {teams.length === 0 && <p className="muted">조회 기간에 팀 배정 건이 없습니다.</p>}
            {teams.length > 0 && (
              <table className="grid stat-table">
                <thead>
                  <tr>
                    <th>팀명</th>
                    <th className="num">실 금액</th>
                    <th className="bar-col" aria-label="막대 그래프" />
                  </tr>
                </thead>
                <tbody>
                  {teams.map((t) => (
                    <tr key={t.team_id}>
                      <td>{t.name}</td>
                      <td className="num">{won(t.amount)}</td>
                      <td className="bar-col">
                        <span
                          className="bar bar--done"
                          style={{ width: barWidth(t.amount, teamMax) }}
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </>
        )}
      </section>
    </div>
  );
}
